import { useEffect, useMemo, useState } from 'react';
import { useFeatureFlags } from '../../public/site/ConfigProvider.js';
import { fetchAddOnsVisibilities, fetchPublicAddOnsVisibilities } from '../fetch.js';
import { filterAddOnVisibilitiesByFeatureFlags } from '../utils/addOnVisibilityUtils.js';
import type { AddOnVisibility } from '../../generated/api/addOnVisibility.js';
import type { AddOnVisibilityOperationType } from '../enums.js';

export interface AddOnVisibilityResult {
  addOnVisibilitiesLoading: boolean;
  addOnVisibilities?: AddOnVisibility[];
  addOnVisibilitiesError?: string;
}

interface CacheEntry {
  promise: Promise<{ addOnVisibilities?: AddOnVisibility[] }>;
  result?: AddOnVisibility[];
  error?: string;
}

// Module-level cache to deduplicate in-flight requests and cache results
// Key format: `${commercialProductCode}-${operationType}-${mdmId}`
const requestCache = new Map<string, CacheEntry>();

const getCacheKey = (
  commercialProductCode: string,
  operationType: AddOnVisibilityOperationType,
  mdmId?: string,
  isAuthenticated = true
) => `${commercialProductCode}-${operationType}-${mdmId ?? ''}-${isAuthenticated}`;

export const useAddOnVisibility = (
  commercialProductCode: string,
  operationType: AddOnVisibilityOperationType,
  preFetchedAddOnVisibilities?: AddOnVisibility[],
  mdmId?: string,
  isAuthenticated = true
): AddOnVisibilityResult => {
  const [addOnVisibilitiesError, setAddOnVisibilitiesError] = useState<string>();
  const [addOnVisibilities, setAddOnVisibilities] = useState<AddOnVisibility[] | undefined>(
    preFetchedAddOnVisibilities
  );
  const [addOnVisibilitiesLoading, setAddOnVisibilitiesLoading] = useState(false);
  const featureFlags = useFeatureFlags();

  const visibleAddOnVisibilities = useMemo(
    () => addOnVisibilities && filterAddOnVisibilitiesByFeatureFlags(addOnVisibilities, featureFlags),
    [addOnVisibilities, featureFlags]
  );

  useEffect(() => {
    // If pre-fetched visibilities are provided, skip the fetch
    if (preFetchedAddOnVisibilities) {
      setAddOnVisibilities(preFetchedAddOnVisibilities);
      return;
    }

    const cacheKey = getCacheKey(commercialProductCode, operationType, mdmId, isAuthenticated);
    const cached = requestCache.get(cacheKey);

    // If we already have a cached result, use it immediately
    if (cached?.result) {
      setAddOnVisibilities(cached.result);
      return;
    }

    // If we have a cached error, set it
    if (cached?.error) {
      setAddOnVisibilitiesError(cached.error);
      return;
    }

    setAddOnVisibilitiesLoading(true);

    // If there's already an in-flight request, reuse it; otherwise create new one
    let cacheEntry = cached;
    if (!cacheEntry) {
      // Anonymous users have no session, so the private endpoint would fail. Use the public
      // metadata endpoint instead so add-ons are still shown on public order pages.
      const promise = isAuthenticated
        ? fetchAddOnsVisibilities(commercialProductCode, operationType, mdmId)
        : fetchPublicAddOnsVisibilities(commercialProductCode, operationType);
      cacheEntry = { promise };
      requestCache.set(cacheKey, cacheEntry);
    }

    cacheEntry.promise
      .then(res => {
        // Update cache with result
        cacheEntry!.result = res.addOnVisibilities;
        setAddOnVisibilities(res.addOnVisibilities);
        setAddOnVisibilitiesLoading(false);
      })
      .catch((e: Error) => {
        // Update cache with error
        cacheEntry!.error = e.message;
        setAddOnVisibilitiesError(e.message);
      })
      .finally(() => setAddOnVisibilitiesLoading(false));
  }, [commercialProductCode, preFetchedAddOnVisibilities, operationType, mdmId, isAuthenticated]);

  return { addOnVisibilities: visibleAddOnVisibilities, addOnVisibilitiesLoading, addOnVisibilitiesError };
};
